/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import PropTypes from 'prop-types';
import Markdown from 'react-markdown'; // https://github.com/remarkjs/react-markdown/tree/5.0.3
import {
  Typography, ProgressStepper, Step, StepButton, StepLabel
} from '@one-thd/sui-atomic-components';

const cleanMarkdownText = (markdownText = '', variant = 'default') => {
  let resultingText = markdownText.replace(/\\n/g, '\n');
  // For prizes, ensure single newlines become paragraph breaks
  // so the title (bold) and description render on separate lines
  if (variant === 'prizesContent') {
    resultingText = resultingText.replace(/\n(?!\n)/g, '\n\n');
  }
  return resultingText;
};

const RenderMarkdown = ({
  markdownText = '',
  variant = 'default',
  disableSpacing = false,
  openLinksInNewTab = false
}) => {
  const renderers = {
    // Custom strong renderer
    strong: ({ node, children, ...props }) => {
      if (variant === 'prizesContent') {
        return (
          <Typography color="brand" weight="bold" component="strong" {...props}>
            {children}
          </Typography>
        );
      }
      return <strong {...props}>{children}</strong>;
    },
    emphasis: ({ node, children, ...props }) => {
      if (variant !== 'stepContent') {
        return <em {...props}>{children}</em>;
      }

      return (
        <span className="sui-text-subtle sui-text-base" {...props}>
          {children}
        </span>
      );
    },
    // Custom link renderer with underline
    link: ({ node, children, href, ...props }) => {
      const linkProps = openLinksInNewTab
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {};
      return (
        <a href={href} className="sui-underline sui-text-brand" {...linkProps} {...props}>
          {children}
        </a>
      );
    },
    // Custom image renderer with conditional styling
    image: ({ node, src, alt, ...props }) => (
      <img
        src={src}
        alt={alt}
        height="auto"
        width="100%"
        className={variant === 'stepContent' ? 'sui-w-full sui-my-6' : ''}
        {...props}
      />
    ),
    // Custom ordered list renderer
    list: ({ node, children, ordered, ...props }) => {
      if (ordered) {
        return (
          <ProgressStepper
            centerEdgeNodes
            nonLinear
            orientation="vertical"
            connector={<></>}
          >
            {children}
          </ProgressStepper>
        );
      }
      return <ul className="sui-list-disc sui-pl-6 sui-mb-4 sui-text-base">{children}</ul>;
    },
    // Custom list item renderer using Step component
    listItem: ({
      node, children, ordered, index, ...props
    }) => {
      if (ordered) {
        return (
          <Step key={index} completed={false}>
            <div data-testid={`sweepstakes-step-instructions-${index}`} style={{ pointerEvents: 'none' }}>
              <StepButton
                text={index + 1}
              />
            </div>
            <StepLabel>
              <div>
                <Typography variant="body-base">
                  {children}
                </Typography>
              </div>
            </StepLabel>
          </Step>
        );
      }
      return <li className="sui-mb-1">{children}</li>;
    },
    // Custom paragraph renderer
    paragraph: ({ node, children, ...props }) => {
      const textVariant = variant === 'stepContent' ? 'body-lg' : 'body-base';
      let spacingClass = disableSpacing ? '' : 'sui-mb-4';
      if (variant === 'prizesContent') spacingClass = 'sui-mb-1';
      return (
        <div className={spacingClass}>
          <Typography variant={textVariant} {...props}>
            {children}
          </Typography>
        </div>
      );
    },
  };

  return (
    <Markdown renderers={renderers}>
      {markdownText}
    </Markdown>
  );
};

export const SweepstakesMarkdown = ({
  markdownText = '',
  variant = 'default',
  disableSpacing = false,
  openLinksInNewTab = false,
  testId = 'sweepstakes-markdown'
}) => {
  const resultingText = cleanMarkdownText(markdownText, variant);
  return (
    <div
      // style={{}}
      data-testid={testId}
    >
      {/* Overrides */}
      {/* <style>{` `}</style> */}
      <RenderMarkdown
        markdownText={resultingText}
        variant={variant}
        disableSpacing={disableSpacing}
        openLinksInNewTab={openLinksInNewTab}
      />
    </div>
  );
};

SweepstakesMarkdown.propTypes = {
  // variant validates specific allowed values ('default' | 'stepContent')
  // Has default value of 'default' in function signature
  // eslint-disable-next-line react/require-default-props
  variant: PropTypes.oneOf(['default', 'stepContent', 'prizesContent']),
  // Other props (markdownText, disableSpacing, openLinksInNewTab, testId) have
  // obvious types inferred from their default values in function signature
};
